import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  TextareaAutosize,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  createConfig,
  getConfigCategoryList,
  getConfigForTable,
  getStageWiseConfigs,
  resetCurrentConfig,
  setCreateConfigStatus,
  setUpdateConfigStatus,
  updateConfig,
} from "../../redux/slices/config";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store/appStore";
import { AddButton } from "../button/button";
import { insertCustomFileNameSelection } from "./customFileName";
import { STATUS } from "../../utils/status";
import SnackbarNotification from "../snackbar/snackbarNotification";
import debounce from "lodash.debounce";
import { getReview } from "../../redux/slices/reviewerSlice";
import { ConfigIds } from "./config";
import RichTextEditor from "../project/textEditor";
import ScoringSelector from "./ScoringSelector";
import ScoringConfigSection from "./ScoringConfigSection";
import { setSelectedStageId } from '../../redux/slices/reviewerSlice/index';
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CustomRichTextEditor from "../CustomRichTextEditor/CustomRichTextEditor";
import { get } from "http";
import AdvancedAiSettings, { AdvancedAiConfig } from "./advancedAiSettings";
import TestAiValidationModal from "./TestAiValidationModal";
import AudioModeSettings, { AudioModeConfig } from "./AudioModeSettings"


type DictTypeContentProps = {
  chipId: string;
  chipLabel: string;
  keys: string[] | null | undefined;
  handleInputChange: (key: string, value: string) => void;
  taskCategory: string;
  interactiveConfig: boolean;
  configIds: ConfigIds[];
  configStatus: any;
  selectedStage: string,
  selectedCategory: string
};

export enum FileType {
  PDF = "pdf",
  DOCX = "docx",
  XLSX = "xlsx",
  JPEG = "jpeg",
  JPG = "jpg",
  EML = "eml",
  PNG = "png",
  Doc = "doc",
  HEIF = "heif",
  HEIC = "heic",
  MP3 = "mp3",
  WAV = "wav",
  AAC = "aac",
  MP4 = "mp4",
  MOV = "mov",
  AVI = "avi",
}

const formatOptions = [
  { label: "Hrs Mins Sec Millisec (HH:MM:SS.mm)", value: "HH:MM:SS.mm" },
  { label: "Hrs Mins Sec (HH:MM:SS)", value: "HH:MM:SS" },
  { label: "Hrs Mins (HH:MM)", value: "HH:MM" },
  { label: "Hrs (HH)", value: "HH" },
  { label: "Mins (MM)", value: "MM" },
  { label: "Sec (SS)", value: "SS" },
  { label: "Millisec (mm)", value: "mm" },
  { label: "Sec Millisec (SS.mm)", value: "SS:mm" },
  { label: "Mins Sec (MM:SS)", value: "MM:SS" },
];
type FormatKey = "star_rating" | "1-5_rating" | "1-10_rating" | "emoji_rating";

type ScoringFormat = {
  label: string;
  scores: (string | { emoji: string; label: string })[];
};
const scoringFormats: Record<FormatKey, ScoringFormat> = {
  "star_rating": {
    label: "★ Star Rating",
    scores: ["Very Poor", "Poor", "Average", "Good", "Very Good"]
  },
  "1-5_rating": {
    label: "1–5 Number Score",
    scores: ["Very Poor", "Poor", "Average", "Good", "Very Good"]
  },
  "1-10_rating": {
    label: "1–10 Score",
    scores: Array.from({ length: 10 }, (_, i) =>
      i < 5 ? ["Very Poor", "Poor", "Average", "Good", "Very Good"][i] : ""
    )
  },
  "emoji_rating": {
    label: "😃 Emoji Score",
    scores: [
      { emoji: "😐", label: "Very Poor" },
      { emoji: "😐", label: "Poor" },
      { emoji: "😐", label: "Average" },
      { emoji: "😐", label: "Good" },
      { emoji: "😐", label: "Very Good" }
    ]
  }
};

const CONFIG_TYPES = [
  { value: "DYNAMIC", label: "Task upload Data" },
  { value: "STATIC", label: "Static" },
  { value: "PREVIOUS_STAGE", label: "Previous stage" },
];

const taskUploadDataString = `Admin can refer to the uploaded data column by using @. Multiple config_name entries can be used in a single text area. Works the same as Previous Stage but uses the upload data source.`
const staticString = `Admin can directly enter the text to show to the Tasker.`
const previousStageString = `Admin can refer to a previous stage’s uploaded data column by using @stage_name.config_identifier. Multiple stage_name and config_name can be used in a single text area.`

const DESCRIPTIONS: Record<string, string> = {
  DYNAMIC: taskUploadDataString,
  PREVIOUS_STAGE: previousStageString,
  STATIC: staticString,
};

const DictTypeContent: React.FC<DictTypeContentProps> = ({
  chipId,
  chipLabel,
  keys,
  handleInputChange,
  taskCategory,
  interactiveConfig = false,
  configIds,
  configStatus,
  selectedStage,
  selectedCategory
}) => {
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  // const [fieldValues, setFieldValues] = useState<{ [key: string]: string }>({ "PDF": "pdf" });
  const [fieldValues, setFieldValues] = useState<{ [key: string]: string | string[] }>({ selected_file_types: chipLabel === 'upload' ? ["pdf"] : [] });
  const [metaDataList, setMetaDataList] = useState<Record<string, string[]>>({
    metadataList: [],
  });
  const createStatus = useSelector((state: RootState) => state.config.createStatus);
  const updateStatus = useSelector(
    (state: RootState) => state.config.updateStatus
  );
  const configValue = useSelector(
    (state: RootState) => state.config.currentConfig
  );
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState<any>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("success");
  const [selectedOption, setSelectedOption] = useState<"TASK" | "REVIEW">(
    "TASK"
  );
  const [order, setOrder] = useState("");
  const [localIsChecked, setLocalIsChecked] = useState<boolean>(false);
  const [grammerCheck, setGrammerCheck] = useState<boolean>(false);
  const [enableUrl, setEnableUrl] = useState<boolean>(false);
  const [configType, setConfigType] = useState<string>("DYNAMIC");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState<{ grammatical_check_language?: string }>({});
  const [startConfig, setStartConfig] = useState<boolean>(false);
  const [autoOpen, setAutoOpen] = useState<boolean>(false);
  const [isMetaData, setIsMetaData] = useState<boolean>(false);
  const [useCustomStorage, setUseCustomStorage] = useState<boolean>(false);
  const [keepFileNameIntact, setKeepFileNameIntact] = useState<boolean>(false);
  const [endConfig, setEndConfig] = useState<boolean>(false);
  const [options, setOptions] = useState<any[]>([]);
  const categoryList = useSelector((state: RootState) => state.config.configCategories);
  const getConfigCategpryListStatus = useSelector((state: RootState) => state.config.getConfigCategoryListStatus);
  const createConfigMessage = useSelector((state: RootState) => state.config.createConfigMessage);
  const updateConfigMessage = useSelector((state: RootState) => state.config.updateConfigMessage);
  const getStageWiseConfigData = useSelector((state: RootState) => state.config.getStageWiseConfigData)
  const [configSticky, setConfigSticky] = useState<boolean>(false);
  const [nextConfigId, setNextConfigId] = useState<ConfigIds>();
  const [selectedFormat, setSelectedFormat] = useState<FormatKey | "">("star_rating");
  const [descriptions, setDescriptions] = useState<any>({});
  const [selectedEmojis, setSelectedEmojis] = useState<{ emoji: string; label: string }[]>([]);
  const path = window.location.pathname.includes('editconfig');

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [pickerVisible, setPickerVisible] = useState(false);
  const [pickerIndex, setPickerIndex] = useState<number | null>(null);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const [defaultValueType, setDefaultValueType] = useState("");
  const [validLlmValues, setValidLlmValues] = useState<any>([{ llmPlatform: [], llmTitle: "", allowEdit: false }]);
  const [showCompilerChip, setShowCompilerChip] = useState<boolean>(false);
  const [llmIntegration, setLlmIntegration] = useState<boolean>(false);
  const [endpointUrlInput, setEndpointUrlInput] = useState<string>("");
  const [showEndpointDropdown, setShowEndpointDropdown] = useState<boolean>(false);
  const [endpointCursorPosition, setEndpointCursorPosition] = useState<number>(0);
  const [enableScoring, setEnableScoring] = useState<boolean>(false);
  const [scoringMode, setScoringMode] = useState<string>('AUTO');
  const [scoringTitle, setScoringTitle] = useState<string>('');
  const endpointInputRef = useRef<HTMLInputElement>(null);
  const [identifierToIdMap, setIdentifierToIdMap] = useState<Record<string, string>>({});
  const [customFileNameInput, setCustomFileNameInput] = useState<string>("");
  const [showCustomFileNameDropdown, setShowCustomFileNameDropdown] = useState<boolean>(false);
  const [customFileNameCursorPosition, setCustomFileNameCursorPosition] = useState<number>(0);
  const customFileNameInputRef = useRef<HTMLInputElement>(null);
  const [customFileNameIdentifierToIdMap, setCustomFileNameIdentifierToIdMap] = useState<Record<string, string>>({});
  const [showAiPanel, setShowAiPanel] = useState(true);
  const [advancedAiConfig, setAdvancedAiConfig] = useState<AdvancedAiConfig | null>(null);
  const [aiValidationInitialConfig, setAiValidationInitialConfig] = useState<AdvancedAiConfig | undefined>(undefined);
  const [showTestAiModal, setShowTestAiModal] = useState(false);
  const [showAudioModePanel, setShowAudioModePanel] = useState(false);
  const [audioModeConfig, setAudioModeConfig] = useState<AudioModeConfig | null>(null);
  const [audioModeInitialConfig, setAudioModeInitialConfig] = useState<AudioModeConfig | undefined>(undefined);
  const [showAudioErrors, setShowAudioErrors] = useState(false);


  let { configRowId } = useParams();

  let missingFields: any = [];


  const filteredConfigs = configIds.filter(d => {
    if (selectedOption === 'REVIEW') {
      return (
        d.type === 'REVIEW' &&
        String(d.stage_id) === String(selectedStage)
      );
    } else {
      return d.type === 'TASK';
    }
  });

  useEffect(() => {
    if (chipLabel === "llm_response" && validLlmValues.length === 0) {
      setValidLlmValues([{ llmPlatform: "", llmTitle: "", allowEdit: false }]);
    }
  }, [chipLabel]);

  const updatePickerPosition = (index: number) => {
    const input = inputRefs.current[index];
    if (input) {
      const rect = input.getBoundingClientRect();
      setPickerPosition({
        top: rect.top + window.scrollY + 35,
        left: rect.left + window.scrollX,
      });
    }
  };

  const handleDescriptionChange = (index: number, value: string) => {
    setDescriptions((prev: any) => ({
      ...prev,
      [selectedFormat]: {
        ...prev[selectedFormat as FormatKey],
        [index]: value,
      },
    }));
  };

  const handleEmojiSelect = (emojiData: any) => {
    if (pickerIndex !== null) {
      const newEmojis = [...selectedEmojis];
      newEmojis[pickerIndex] = emojiData.emoji;
      setSelectedEmojis(newEmojis);
      setPickerVisible(false);
    }
  };

  const scoreValuesJson = () => {
    const formatKeysMap: Record<FormatKey, string> = {
      "star_rating": "star_rating",
      "1-5_rating": "1-5_rating",
      "1-10_rating": "1-10_rating",
      "emoji_rating": "emoji_rating",
    };

    if (!selectedFormat) return {};

    const scores = scoringFormats[selectedFormat].scores;
    const formatDescriptions = descriptions[selectedFormat] || {};

    const values = scores.map((score: any, index: number) => {
      if (selectedFormat == "emoji_rating") {
        const emoji = selectedEmojis[index];
        return {
          key: emoji || "",
          emoji: emoji || "",
          description: formatDescriptions[index] || "",
        };
      }

      const key =
        selectedFormat === "star_rating"
          ? "*".repeat(index + 1)
          : String(index + 1);

      const value =
        formatDescriptions[index] ??
        (typeof score === "string" ? score : score.label);

      return { key, value };
    });

    return {
      [formatKeysMap[selectedFormat]]: values,
    };
  };

  const IMAGE_TYPES = ["jpeg", "jpg", "png", "heic", "heif"];

  const hasAnyImageType =
    Array.isArray(fieldValues?.selected_file_types) &&
    fieldValues.selected_file_types.length > 0 &&
    fieldValues.selected_file_types.every((type: string) =>
      IMAGE_TYPES.includes(type.toLowerCase())
    );

  useEffect(() => {
    if (!hasAnyImageType && isMetaData) {
      setIsMetaData(false);
    }
  }, [hasAnyImageType]);

  const fieldValuesAndScoreValues = () => {
    const scoreValues = scoreValuesJson();

    if (!Object.keys(scoreValues).length) return fieldValues;

    const key = Object.keys(scoreValues)[0];
    const value = scoreValues[key];

    const { score_values, ...restFieldValues } = fieldValues;

    return {
      ...restFieldValues,
      score_values: {
        key,
        value
      }
    };
  }

  // const handleStageOptionChange = (event: any) => {
  //   setSelectedStage(event.target.value);
  // };

  useEffect(() => {
    if (selectedStage) {
      dispatch(getStageWiseConfigs({ id: selectedStage }))
    }
  }, [selectedStage])

  useEffect(() => {
    dispatch(getReview({ project_id: id || "" }));
  }, [dispatch, id]);

  useEffect(() => {
    if (configRowId) {
      dispatch(getConfigForTable({ project_id: id, id: configRowId }));
    } else {
      dispatch(resetCurrentConfig());
    }
  }, [configRowId, id]);

  useEffect(() => {
    // Always fetch config category list for dropdown options (needed for endpoint URL autocomplete)
    if (id) {
      dispatch(getConfigCategoryList({ project_id: id }));
    }
  }, [id, dispatch]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setLocalIsChecked(checked);
  };

  const handleGrammerCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setGrammerCheck(checked);

    if (!checked) {
      if (!checked) {
        setSelectedLanguage({ grammatical_check_language: "" });
      }
    }
  };

  const handleEnableUrlChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setEnableUrl(checked);
  };

  const getConfigFromReducer = useSelector((state: RootState) => state.config.currentConfig);
  const config_id = getConfigFromReducer.id;

  console.log({getConfigFromReducer})


  useEffect(() => {
    if (getConfigFromReducer && getConfigFromReducer.value) {
      // Never hydrate from Redux state when creating a new config (no configRowId).
      // The stale data from a previous edit session would otherwise overwrite the
      // default field values (e.g. selected_file_types: ["pdf"]) before resetCurrentConfig
      // can propagate through a re-render.
      if (!configRowId) return;
      // Skip stale Redux data — only hydrate when the loaded config matches the URL's configRowId.
      if (configRowId && getConfigFromReducer.id !== configRowId) return;

      const configValues = getConfigFromReducer.value;
      const configKeys = Object.keys(configValues);
      const initialFieldValues: { [key: string]: string } = {};

      configKeys.forEach((key) => {
        initialFieldValues[key] = configValues[key];
      });

      // For URL config, add prompt to fieldValues from root level
      if (chipLabel === "url" && getConfigFromReducer.prompt) {
        initialFieldValues.prompt = getConfigFromReducer.prompt;
      }

      setFieldValues(initialFieldValues);
      if (initialFieldValues.language) {
        setShowCompilerChip(hasCompiler(initialFieldValues.language));
      }

      setStartConfig(getConfigFromReducer.is_starting_config);
      setEndConfig(getConfigFromReducer.is_end_config);
      setSelectedOption(getConfigFromReducer.type);
      setOrder(getConfigFromReducer.order);
      setFormValues({
        identifier: getConfigFromReducer.identifier,
        long_description: getConfigFromReducer.long_description,
      });
      setLocalIsChecked(getConfigFromReducer.required);
      setConfigType(getConfigFromReducer.default_value_type || "DYNAMIC");
      setNextConfigId(
        configIds.find(
          (item) => item.id === getConfigFromReducer.next_config_id
        )
      );
      setGrammerCheck(getConfigFromReducer.grammatical_check);
      setEnableUrl(getConfigFromReducer.value?.is_url_enabled);
      setIsMetaData(getConfigFromReducer?.check_metadata_presence || false);

      // For upload config, check if custom storage is enabled and populate fields
      if (chipLabel === "upload") {
        // Check both root level and value object for custom storage fields (backend may return in either location)
        const customFields = {
          access_key_id: getConfigFromReducer.custom_upload_storage_provider_access_key_id ||
            getConfigFromReducer.value?.custom_upload_storage_provider_access_key_id,
          secret_key: getConfigFromReducer.custom_upload_storage_provider_secret_access_key ||
            getConfigFromReducer.value?.custom_upload_storage_provider_secret_access_key,
          region: getConfigFromReducer.custom_upload_storage_provider_region ||
            getConfigFromReducer.value?.custom_upload_storage_provider_region,
          bucket: getConfigFromReducer.custom_upload_storage_provider_bucket_name ||
            getConfigFromReducer.value?.custom_upload_storage_provider_bucket_name,
          endpoint: getConfigFromReducer.custom_upload_storage_provider_endpoint_url ||
            getConfigFromReducer.value?.custom_upload_storage_provider_endpoint_url,
        };

        const hasCustomStorage = customFields.access_key_id || customFields.secret_key ||
          customFields.region || customFields.bucket || customFields.endpoint;

        setUseCustomStorage(!!hasCustomStorage);

        // Populate custom storage fields
        if (hasCustomStorage) {
          initialFieldValues.custom_upload_storage_provider_access_key_id = customFields.access_key_id || "";
          initialFieldValues.custom_upload_storage_provider_secret_access_key = customFields.secret_key || "";
          initialFieldValues.custom_upload_storage_provider_region = customFields.region || "";
          initialFieldValues.custom_upload_storage_provider_bucket_name = customFields.bucket || "";
          initialFieldValues.custom_upload_storage_provider_endpoint_url = customFields.endpoint || "";

          // Set endpoint URL input for autocomplete
          if (customFields.endpoint) {
            // Parse endpoint URL to extract identifier-to-config-id mapping
            // Pattern: configId.{identifier}
            const identifierPattern = /([a-f0-9-]+)\.(\{[^}]+\})/g;
            const mapping: Record<string, string> = {};
            let match;
            let displayUrl = customFields.endpoint;

            while ((match = identifierPattern.exec(customFields.endpoint)) !== null) {
              const configId = match[1];
              const identifier = match[2].replace(/{|}/g, ''); // Remove braces
              mapping[identifier] = configId;
              // Remove configId from display URL
              displayUrl = displayUrl.replace(`${configId}.{${identifier}}`, `{${identifier}}`);
            }

            setIdentifierToIdMap(mapping);
            setEndpointUrlInput(displayUrl);
          }

          // Update fieldValues with custom storage fields
          setFieldValues((prev) => ({
            ...prev,
            ...initialFieldValues
          }));
        }

        // Set keep file name intact checkbox - check both root level and value object
        const keepFileNameIntactValue = getConfigFromReducer.keep_file_name_intact ||
          getConfigFromReducer.value?.keep_file_name_intact;
        setKeepFileNameIntact(keepFileNameIntactValue === "true" || keepFileNameIntactValue === true);

        const savedCustomFileName = getConfigFromReducer.custom_file_name ||
          getConfigFromReducer.value?.custom_file_name;
        if (savedCustomFileName) {
          const identifierPattern = /([a-f0-9-]+)\.\{([^}]+)\}/g;
          const mapping: Record<string, string> = {};
          let match;
          let displayValue = savedCustomFileName;
          while ((match = identifierPattern.exec(savedCustomFileName)) !== null) {
            const configId = match[1];
            const identifier = match[2];
            mapping[identifier] = configId;
            displayValue = displayValue.replace(`${configId}.{${identifier}}`, `{${identifier}}`);
          }
          setCustomFileNameIdentifierToIdMap(mapping);
          setCustomFileNameInput(displayValue);
        } else {
          setCustomFileNameInput("");
          setCustomFileNameIdentifierToIdMap({});
        }

        // Hydrate Advanced AI Validation settings from saved config
        const av = getConfigFromReducer.ai_validation;
        if (av) {
          const opRevMap: Record<string, string> = { GTE: '>=', LTE: '<=', EQ: '==', Exists: 'Exists', IN: 'In' };
          const normalizeMultiValue = (value: unknown): string => {
            if (Array.isArray(value)) {
              return value.map(v => String(v).trim()).filter(Boolean).join(', ');
            }
            return value != null ? String(value) : '';
          };
          const schedDown = (s: string): 'daily' | 'manual' | 'on_image_upload' | 'on_task_submission' => {
            const lower = s?.toLowerCase() ?? '';
            if (lower === 'manual') return 'manual';
            if (lower === 'on_image_upload') return 'on_image_upload';
            if (lower === 'on_task_submission') return 'on_task_submission';
            return 'daily';
          };
          const fpRevMap: Record<string, string> = { SEND_TO_NEXT_STAGE: 'send_to_next', MARK_COMPLETE: 'mark_accepted' };
          const mapMeta = (id: string, label: string, field: any, opOpts: string[], vt: any) => ({
            id, label,
            enabled: field?.enabled ?? false,
            operator: opRevMap[field?.operator] ?? field?.operator ?? (opOpts[0] ?? ''),
            value: normalizeMultiValue(field?.value),
            required: false,
            operatorOptions: opOpts,
            valueType: vt,
          });
          const mapRangeMeta = (id: string, label: string, field: any) => ({
            id, label,
            enabled: field?.enabled ?? false,
            operator: '',
            value: '',
            required: false,
            operatorOptions: [],
            valueType: 'number' as const,
            minValue: field?.min_value != null ? String(field.min_value) : '',
            maxValue: field?.max_value != null ? String(field.max_value) : '',
          });
          setAiValidationInitialConfig({
            metadataFields: [
              mapRangeMeta('resolution', 'Resolution (MP)', av.mv_resolution),
              mapRangeMeta('filesize',   'File Size (MB)',  av.mv_file_size),
              mapMeta('location',    'Location',        av.mv_location,    [], 'text'),
              { ...mapMeta('fileformat', 'File Format', av.mv_file_format, [], 'multiselect'), enabled: true, operatorOptions: [] },
              {
                ...mapMeta('capturedate', 'Capture Date', av.mv_capture_date, [], 'days_select'),
                value: av.mv_capture_date?.value != null && av.mv_capture_date?.value !== ''
                  ? `last_${av.mv_capture_date.value}`
                  : '',
              },
              mapMeta('devicemake', 'Device Make', av.mv_device_make, [], 'multiselect'),
              mapMeta('devicemodel', 'Device Model', av.mv_device_model, [], 'multiselect'),
            ],
            customPrompt: {
              enabled: av.cpv_enabled ?? false,
              text: av.cpv_prompt ?? '',
              schedule: schedDown(av.cpv_schedule),
            },
            additionalValidations: [
              { id: 'blur',           label: 'Blur Check',        enabled: av.av_blur_enabled           ?? false, schedule: schedDown(av.av_blur_schedule) },
              {
                id: 'duplication', label: 'Duplication Check',
                enabled: av.av_duplication_enabled ?? false,
                schedule: schedDown(av.av_duplication_schedule),
                duplicationType: av.duplication_scope_type === 'CUSTOM_TEMPLATE_PATHS' ? 'custom_template_path' : 'custom_literal_path',
                literalPaths: (av.duplication_scope_literal_values?.length ?? 0) > 0 ? av.duplication_scope_literal_values : [''],
                templatePaths: (av.duplication_scope_template_values?.length ?? 0) > 0 ? av.duplication_scope_template_values : [''],
                resolutionMode: av.duplication_scope_resolution_mode === 'ALL_RESOLVED_PATHS' ? 'all_resolved_paths' : 'current_path_only',
              },
              { id: 'pii',            label: 'PII Detection',     enabled: av.av_pii_enabled            ?? false, schedule: schedDown(av.av_pii_schedule) },
              { id: 'content_safety', label: 'Content Safety',    enabled: av.av_content_safety_enabled ?? false, schedule: schedDown(av.av_content_safety_schedule) },
            ],
            aiRules: {
              failThreshold:    String(av.vr_rejection_threshold ?? 70),
              partialMin:       String(av.vr_partial_pass_min    ?? 70),
              partialMax:       String(av.vr_partial_pass_max    ?? 95),
              fullPassThreshold:String(av.vr_full_pass_threshold ?? 95),
              fullPassAction:   (fpRevMap[av.vr_on_full_pass] ?? 'send_to_next') as any,
            },
            model: av?.ai_validation_model ?? av?.model ?? '',
          });
        }
      }

      setSelectedLanguage(getConfigFromReducer);
      setConfigSticky(getConfigFromReducer.is_sticky);
      setDefaultValueType(getConfigFromReducer.default_value)
      setSelectedStageId(getConfigFromReducer.stage_id);
      setValidLlmValues(getConfigFromReducer?.value?.validLlmValues || [{ llmPlatform: [], llmTitle: "", allowEdit: false }]);
      setAutoOpen(getConfigFromReducer.auto_open);
      setLlmIntegration(getConfigFromReducer?.llm_integration || false);
      if (getConfigFromReducer.id && chipLabel === 'upload') {
        setShowAiPanel(true);
      }
      setEnableScoring(getConfigFromReducer.enable_scoring || false);
      setScoringMode(getConfigFromReducer.scoring_mode || 'AUTO');
      setScoringTitle(getConfigFromReducer.scoring_title || '');

      // Hydrate Audio Mode Settings for audio_recording config
      // Supports new flat format (fields directly on value) and legacy nested (audio_mode_settings)
      if (chipLabel === 'audio_recording') {
        const v = getConfigFromReducer.value;
        // Prefer new flat format; fall back to legacy nested wrapper
        const ams = v?.audio_mode != null ? v : v?.audio_mode_settings;
        if (ams) {
          // Map API audio_mode string back to internal AudioMode type
          const API_TO_AUDIO_MODE: Record<string, string> = {
            only_audio: 'audio_recording_only',
            only_script: 'script_display_only',
            script_and_recording: 'script_display_with_audio_recording',
            Both: 'script_display_with_audio_recording', // legacy value
            // legacy pass-through values
            audio_recording_only: 'audio_recording_only',
            script_display_only: 'script_display_only',
            script_display_with_audio_recording: 'script_display_with_audio_recording',
          };
          setAudioModeInitialConfig({
            audioMode: (API_TO_AUDIO_MODE[ams.audio_mode] ?? 'audio_recording_only') as any,
            scriptSourceContent: ams.script_source_content ?? '',
            allowUploadRecordedFile: ams.allow_upload_recorded_file ?? false,
            recordedFileMaxSize: ams.recorded_file_max_size ?? null,
            customisableMarkers: (ams.customisable_markers ?? []).map((m: any) => ({
              ...m,
              duration_ms: m.duration_ms ?? m.duration_seconds ?? '',
            })),
            defaultDurationMs: ams.default_duration_ms != null ? String(ams.default_duration_ms) : '',
            backgroundNoise: ams.background_noise ?? [],
            audioTags: ams.audio_tags ?? [],
            playSoundBetweenUtterances: ams.play_sound_between_utterances ?? false,
            utteranceSoundLink: ams.utterance_sound_link ?? '',
            utteranceSoundType: ams.utterance_sound_type ?? '',
            pauseDurations: ams.pause_duration ?? [],
          });
          setShowAudioModePanel(true);
        }
      }



      const scoreKey = getConfigFromReducer?.value?.score_values?.key;
      const scoreValueArray = getConfigFromReducer?.value?.score_values?.value;

      if (scoreKey && Array.isArray(scoreValueArray)) {
        setSelectedFormat(scoreKey);
        const descriptionss = scoreValueArray.reduce(
          (acc: Record<number, string>, curr: any, index: number) => {
            if (scoreKey === "emoji_rating") {
              acc[index] = curr.description || "";
            } else {
              acc[index] = curr.value || "";
            }
            return acc;
          },
          {}
        );
        setDescriptions((prev: any) => ({
          ...prev,
          [scoreKey]: descriptionss,
        }));

        const emojis = scoreValueArray.map((item: any) => item.emoji || "");
        const hasAnyEmoji = emojis.some((emoji) => emoji);
        if (hasAnyEmoji) {
          setSelectedEmojis(emojis);
        }
      }
    }
  }, [getConfigFromReducer, configIds]);

  useEffect(() => {
    if (!getConfigFromReducer) return;
    if (configRowId && getConfigFromReducer.id !== configRowId) return;

    const metaList = getConfigFromReducer.metadata_presence_list;

    setMetaDataList({
      metadataList: Array.isArray(metaList) ? metaList : [],
    });
  }, [getConfigFromReducer]);

  const handleStartConfigCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setStartConfig(checked);
  };

  const handleAutoOpenCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setAutoOpen(checked);
  };

  const handleMetaDataCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;

    setIsMetaData(checked);
    if (!checked) {
      setMetaDataList({});
    }
  };

  const handleCustomStorageCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setUseCustomStorage(checked);

    if (checked) {
      handleFieldChange('upload_storage', 'CUSTOM');
    } else {
      handleFieldChange('upload_storage', 'DEFAULT');
      // Clear custom storage fields when unchecked
      handleFieldChange('custom_upload_storage_provider_access_key_id', '');
      handleFieldChange('custom_upload_storage_provider_secret_access_key', '');
      handleFieldChange('custom_upload_storage_provider_region', '');
      handleFieldChange('custom_upload_storage_provider_bucket_name', '');
      handleFieldChange('custom_upload_storage_provider_endpoint_url', '');
    }
  };

  const handleKeepFileNameIntactChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setKeepFileNameIntact(checked);
    handleFieldChange('duplicate_file_name', checked.toString());
  };

  const handleClearSelectionNextConfig = () => {
    setNextConfigId(undefined);
  };

  const handleSnackbar = (
    message: string,
    severity: "success" | "error" | "info" | "warning"
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const debouncedResults = useMemo(() => {
    return debounce(handleSearchChange, 300);
  }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  useEffect(() => {
    if (categoryList.length > 0) {
      setOptions(categoryList);
    }
  }, [categoryList]);

  const buildAiValidationPayload = (config: AdvancedAiConfig) => {
    const operatorMap: Record<string, string> = {
      '>=': 'GTE',
      '<=': 'LTE',
      '==': 'EQ',
      'Exists': 'Exists',
      'In': 'IN',
      'EXISTS': 'Exists',
    };
    const scheduleUpper = (s: string) => s.toUpperCase();
    const fullPassActionMap: Record<string, string> = {
      send_to_next: 'SEND_TO_NEXT_STAGE',
      mark_accepted: 'MARK_COMPLETE',
    };
    const getMeta = (id: string) => config.metadataFields.find(f => f.id === id);
    const getAv = (id: string) => config.additionalValidations.find(v => v.id === id);
    const normalizedCustomPrompt = config.customPrompt.text?.trim() ?? '';
    const normalizeMultiSelectValue = (value: unknown): string => {
      if (Array.isArray(value)) {
        return value.map(v => String(v).trim()).filter(Boolean).join(', ');
      }
      return value != null ? String(value) : '';
    };
    const normalizeMultiSelectArray = (value: unknown): string[] | null => {
      if (Array.isArray(value)) {
        const normalizedValues = value.map(v => String(v).trim()).filter(Boolean);
        return normalizedValues.length > 0 ? normalizedValues : null;
      }

      if (typeof value === 'string') {
        const normalizedValues = value.split(',').map(v => v.trim()).filter(Boolean);
        return normalizedValues.length > 0 ? normalizedValues : null;
      }

      return null;
    };

    const resolution  = getMeta('resolution');
    const filesize    = getMeta('filesize');
    const location    = getMeta('location');
    const fileformat  = getMeta('fileformat');
    const capturedate = getMeta('capturedate');
    const devicemake  = getMeta('devicemake');
    const devicemodel = getMeta('devicemodel');
    const blur          = getAv('blur');
    const pii           = getAv('pii');
    const duplication   = getAv('duplication');
    const contentSafety = getAv('content_safety');

    return {
      enabled: true,
      ...(resolution && {
        mv_resolution: {
          enabled: resolution.enabled,
          ...(resolution.minValue && { min_value: resolution.minValue }),
          ...(resolution.maxValue && { max_value: resolution.maxValue }),
        },
      }),
      ...(filesize && {
        mv_file_size: {
          enabled: filesize.enabled,
          ...(filesize.minValue && { min_value: filesize.minValue }),
          ...(filesize.maxValue && { max_value: filesize.maxValue }),
        },
      }),
      ...(location && {
        mv_location: {
          enabled: location.enabled,
          value: location.enabled ? location.value || null : null,
        },
      }),
      ...(fileformat && {
        mv_file_format: {
          enabled: true,
          value: normalizeMultiSelectValue(fileformat.value) || null,
        },
      }),
      ...(capturedate && {
        mv_capture_date: {
          enabled: capturedate.enabled,
          value: capturedate.enabled && capturedate.value
            ? parseInt(capturedate.value.toString().replace('last_', ''), 10) || null
            : null,
        },
      }),
      ...(devicemake && {
        mv_device_make: {
          enabled: devicemake.enabled,
          value: devicemake.enabled
            ? normalizeMultiSelectArray(devicemake.value)
            : null,
        },
      }),
      ...(devicemodel && {
        mv_device_model: {
          enabled: devicemodel.enabled,
          value: devicemodel.enabled
            ? normalizeMultiSelectArray(devicemodel.value)
            : null,
        },
      }),
      cpv_enabled: config.customPrompt.enabled,
      cpv_prompt: config.customPrompt.enabled ? normalizedCustomPrompt : null,
      cpv_schedule: config.customPrompt.enabled ? scheduleUpper(config.customPrompt.schedule) : null,
      ...(blur && {
        av_blur_enabled: blur.enabled,
        av_blur_schedule: scheduleUpper(blur.schedule),
      }),
      ...(pii && {
        av_pii_enabled: pii.enabled,
        av_pii_schedule: scheduleUpper(pii.schedule),
      }),
      ...(duplication && {
        av_duplication_enabled: duplication.enabled,
        av_duplication_schedule: scheduleUpper(duplication.schedule),
        ...(duplication.enabled && {
          duplication_scope_type: duplication.duplicationType === 'custom_template_path'
            ? 'CUSTOM_TEMPLATE_PATHS'
            : 'CUSTOM_LITERAL_PATHS',
          duplication_scope_literal_values: duplication.duplicationType === 'custom_literal_path'
            ? (duplication.literalPaths ?? []).filter(p => p.trim() !== '')
            : [],
          duplication_scope_template_values: duplication.duplicationType === 'custom_template_path'
            ? (duplication.templatePaths ?? []).filter(p => p.trim() !== '')
            : [],
          duplication_scope_resolution_mode: duplication.duplicationType === 'custom_template_path'
            ? (duplication.resolutionMode === 'all_resolved_paths' ? 'ALL_RESOLVED_PATHS' : 'CURRENT_PATH_ONLY')
            : null,
        }),
      }),
      ...(contentSafety && {
        av_content_safety_enabled: contentSafety.enabled,
        av_content_safety_schedule: scheduleUpper(contentSafety.schedule),
      }),
      vr_rejection_threshold: parseFloat(config.aiRules.failThreshold) || 0,
      vr_partial_pass_min: parseFloat(config.aiRules.partialMin) || 0,
      vr_partial_pass_max: parseFloat(config.aiRules.partialMax) || 0,
      vr_full_pass_threshold: parseFloat(config.aiRules.fullPassThreshold) || 0,
      vr_on_full_pass: fullPassActionMap[config.aiRules.fullPassAction] ?? config.aiRules.fullPassAction.toUpperCase(),
      ...(config.model && { ai_validation_model: config.model }),
      pipeline_option: config.pipelineOption ?? 'pipeline1',
    };
  };

  const handleSubmit = () => {
    if (!formValues.identifier) missingFields.push('Unique Identifier');
    if (!fieldValues.title) missingFields.push('Title');
    if (!fieldValues.description && chipLabel !== 'content') missingFields.push('Description');
    if (!defaultValueType && (!interactiveConfig || chipLabel === "edit_content" || chipLabel === "edit_code" || chipLabel === "edit_latex")) missingFields.push('Config Data Type');

    if (chipLabel === 'upload') {
      if (!fieldValues.min_file_no) missingFields.push('Min File Number');
      if (!fieldValues.max_file_no) missingFields.push('Max File Number');
      if (Number(fieldValues.min_file_no) > Number(fieldValues.max_file_no)) {
        handleSnackbar('Min file number should be less than or equal to max file number', 'error');
        return;
      }
    }
    if (chipLabel === 'code' || chipLabel === 'edit_code') {
      if (!fieldValues.language) missingFields.push('Language');
    }
    if ((chipLabel === 'timestamp' || chipLabel === 'video') && !fieldValues.format) missingFields.push('Time Format');
    if (chipLabel === 'llm_response') {
      if (validLlmValues.map(((item: any) => item.llmTitle)).filter(((item: any) => item !== "")).length !== validLlmValues.length) missingFields.push('LLM Title');
      if (validLlmValues.map(((item: any) => item.llmPlatform)).filter(((item: any) => item.length !== 0)).length !== validLlmValues.length) missingFields.push('LLM Platform');
    }
    if (chipLabel === 'url' && llmIntegration && !fieldValues.prompt) {
      handleSnackbar('Please enter a prompt for LLM Integration', 'error');
      return;
    }
    if (
      chipLabel === 'upload' &&
      advancedAiConfig?.customPrompt?.enabled &&
      !advancedAiConfig.customPrompt.text?.trim()
    ) {
      handleSnackbar('Please enter a Custom Prompt for AI Validation (required when prompt validation is enabled)', 'error');
      return;
    }
    if (chipLabel === 'upload' && advancedAiConfig?.hasMetadataErrors) {
      handleSnackbar('Please fill in all enabled metadata fields before saving.', 'error');
      return;
    }
    if (chipLabel === 'upload' && advancedAiConfig?.hasDuplicationPathError) {
      handleSnackbar('Please enter at least one Duplication Check path before saving.', 'error');
      return;
    }
    const fileFormatField = advancedAiConfig?.metadataFields?.find(f => f.id === 'fileformat');
    if (chipLabel === 'upload' && fileFormatField?.enabled && !fileFormatField.value?.trim()) {
      handleSnackbar('Please select at least one File Format in Advanced Validation.', 'error');
      return;
    }
    if (enableScoring && scoringMode === 'MANUAL' && !scoringTitle.trim()) {
      missingFields.push('Scoring Title');
    }

    if (chipLabel === 'audio_recording' && audioModeConfig) {
      const mode = audioModeConfig.audioMode;
      if (mode === 'script_display_only' || mode === 'script_display_with_audio_recording') {
        const scriptVal = (audioModeConfig.scriptSourceContent ?? '').match(/\{[^}]+\}/);
        if (!scriptVal) missingFields.push('Script Source');
        const hasEmptyMarker = (audioModeConfig.customisableMarkers ?? []).some(
          (m) => !m.title.trim() || !m.value.trim()
        );
        if (hasEmptyMarker) missingFields.push('Enter Tags (all rows require Title and Value)');
        const hasEmptyNoise = (audioModeConfig.backgroundNoise ?? []).some((n) => !n.link);
        if (hasEmptyNoise) missingFields.push('Background Noise (audio file required per row)');
        const hasEmptyAudioTag = (audioModeConfig.audioTags ?? []).some(
          (t) => !t.title.trim() || !t.value.trim() || !t.link
        );
        if (hasEmptyAudioTag) missingFields.push('Audio Tags (all rows require Title, Value, and file)');
        const hasEmptyPause = (audioModeConfig.pauseDurations ?? []).some(
          (p) => !(p.title ?? '').trim() || !(p.value ?? '').trim() || !(p.duration_seconds ?? '').trim()
        );
        if (hasEmptyPause) missingFields.push('Pause Durations (all rows require Title, Value, and Duration)');
      }
      if (missingFields.length > 0) {
        setShowAudioErrors(true);
        if (!showAudioModePanel) setShowAudioModePanel(true);
      }
    }

    if (missingFields.length > 0) {
      handleSnackbar(`Please fill required fields: ${missingFields.join(', ')}`, 'error');
      return;
    }

    const fieldValuesWithScore = fieldValuesAndScoreValues();
    let valueData: any = {};
    let promptValue = "";

    if (chipLabel === "llm_response") {
      valueData = { ...fieldValues, ...(validLlmValues.length > 0 && { validLlmValues }) };
    } else if (chipLabel === "url") {
      const { prompt, llm_integration, ...restFieldValues } = fieldValues;
      promptValue = prompt as string || "";
      valueData = restFieldValues;
    } else if (chipLabel === 'audio_recording') {
      // Map internal AudioMode type to API values
      const AUDIO_MODE_TO_API: Record<string, string> = {
        audio_recording_only: 'only_audio',
        script_display_only: 'only_script',
        script_display_with_audio_recording: 'script_and_recording',
      };
      const stripScriptHtml = (html: string): string => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return (tmp.textContent ?? tmp.innerText ?? '').replace(/\u00a0/g, ' ').trim();
      };
      const audioSettings = audioModeConfig
        ? {
            audio_mode: AUDIO_MODE_TO_API[audioModeConfig.audioMode] ?? audioModeConfig.audioMode,
            script_source_content: stripScriptHtml(audioModeConfig.scriptSourceContent ?? ''),
            allow_upload_recorded_file: audioModeConfig.allowUploadRecordedFile ?? false,
            ...(audioModeConfig.allowUploadRecordedFile && audioModeConfig.recordedFileMaxSize != null
              ? { recorded_file_max_size: audioModeConfig.recordedFileMaxSize }
              : {}),
            customisable_markers: (audioModeConfig.customisableMarkers ?? []).map(({ _fileName: _f, _uploading: _u, duration_seconds: _ds, ...rest }: any) => rest),
            ...(audioModeConfig.defaultDurationMs
              ? { default_duration_ms: Number(audioModeConfig.defaultDurationMs) }
              : {}),
            background_noise: (audioModeConfig.backgroundNoise ?? []).map(({ _fileName: _f, _uploading: _u, ...rest }: any) => rest),
            audio_tags: (audioModeConfig.audioTags ?? []).map(({ _fileName: _f, _uploading: _u, ...rest }: any) => rest),
            play_sound_between_utterances: audioModeConfig.playSoundBetweenUtterances ?? false,
            ...(audioModeConfig.playSoundBetweenUtterances && audioModeConfig.utteranceSoundLink
              ? {
                  utterance_sound_link: audioModeConfig.utteranceSoundLink,
                  utterance_sound_type: audioModeConfig.utteranceSoundType ?? '',
                }
              : {}),
            pause_duration: audioModeConfig.pauseDurations ?? [],
          }
        : {};
      const { default_duration_seconds: _dds, ...fieldValuesClean } = fieldValues as any;
      valueData = { ...fieldValuesClean, ...audioSettings };
    } else {
      valueData = chipLabel === "score"
        ? fieldValuesWithScore
        : fieldValues;
    }

    // For upload config, extract custom storage fields from valueData to send at root level
    let customStorageFields: any = {};
    if (chipLabel === "upload") {
      const {
        custom_upload_storage_provider_access_key_id,
        custom_upload_storage_provider_secret_access_key,
        custom_upload_storage_provider_region,
        custom_upload_storage_provider_bucket_name,
        custom_upload_storage_provider_endpoint_url,
        duplicate_file_name, // Also extract this to prevent it from being in value object
        upload_storage, // Extract to prevent it from being in value object
        keep_file_name_intact, // Also extract this to prevent it from being in value object
        selected_file_types, // Extract to prevent it from being in value object
        custom_file_name, // Extract to prevent it from being in value object

        ...restValueData
      } = valueData;

      // Transform custom file name to include config IDs before identifier variables
      let transformedCustomFileName = customFileNameInput;
      if (transformedCustomFileName && Object.keys(customFileNameIdentifierToIdMap).length > 0) {
        Object.entries(customFileNameIdentifierToIdMap).forEach(([identifier, configId]) => {
          const pattern = new RegExp(`\\{${identifier}\\}`, 'g');
          transformedCustomFileName = transformedCustomFileName.replace(pattern, `${configId}.{${identifier}}`);
        });
      }

      // Transform endpoint URL to include config IDs before identifiers
      let transformedEndpointUrl = custom_upload_storage_provider_endpoint_url;
      if (transformedEndpointUrl && Object.keys(identifierToIdMap).length > 0) {
        // Replace {identifier} with configId.{identifier}
        Object.entries(identifierToIdMap).forEach(([identifier, configId]) => {
          const pattern = new RegExp(`\\{${identifier}\\}`, 'g');
          transformedEndpointUrl = transformedEndpointUrl.replace(pattern, `${configId}.{${identifier}}`);
        });
      }


      customStorageFields = {
        // When custom storage is enabled, send the actual field values from fieldValues directly
        ...(useCustomStorage ? {
          custom_upload_storage_provider_access_key_id: fieldValues.custom_upload_storage_provider_access_key_id || "",
          custom_upload_storage_provider_secret_access_key: fieldValues.custom_upload_storage_provider_secret_access_key || "",
          custom_upload_storage_provider_region: fieldValues.custom_upload_storage_provider_region || "",
          custom_upload_storage_provider_bucket_name: fieldValues.custom_upload_storage_provider_bucket_name || "",
          custom_upload_storage_provider_endpoint_url: transformedEndpointUrl || "",
        } : {
          // When custom storage is disabled, explicitly clear all fields
          custom_upload_storage_provider_access_key_id: "",
          custom_upload_storage_provider_secret_access_key: "",
          custom_upload_storage_provider_region: "",
          custom_upload_storage_provider_bucket_name: "",
          custom_upload_storage_provider_endpoint_url: "",
        }),
        keep_file_name_intact: keepFileNameIntact.toString(),
        upload_storage: useCustomStorage ? 'CUSTOM' : 'DEFAULT',
        custom_file_name: transformedCustomFileName || "",
      };

      // Update valueData to exclude these fields
      valueData = restValueData;
    }

    const initialValues = {
      ...(config_id && { id: config_id }),
      ...(id && { project_id: id }),
      ...(taskCategory && { task_category_id: taskCategory }),
      ...(chipId && { config_id: chipId }),
      ...(chipLabel && { name: chipLabel }),
      ...(valueData && {
        value: {
          ...valueData,
          ...(chipLabel === "content" && typeof enableUrl === "boolean" && {
            is_url_enabled: enableUrl,
          }),
        },
      }),
      ...(chipLabel === "url" && typeof llmIntegration === "boolean" && {
        llm_integration: llmIntegration,
      }),
      ...(chipLabel === "url" && promptValue && {
        prompt: promptValue,
      }),
      //  ...(chipLabel === "score" ? { type: "REVIEW" } : (selectedOption && { type: selectedOption })),
      ...(selectedCategory !== "" && { type: selectedCategory }),
      ...(formValues.identifier && { identifier: formValues.identifier }),
      // Explicitly set next_config_id: null when cleared during update
      ...(config_id ? { next_config_id: nextConfigId?.id || null } : (nextConfigId && { next_config_id: nextConfigId?.id })),
      ...(order && Number(order) !== 0 && { order: Number(order) }),
      ...(typeof localIsChecked === "boolean" && { required: localIsChecked }),
      ...(formValues.long_description && { long_description: formValues.long_description }),
      ...(typeof grammerCheck === "boolean" && { grammatical_check: grammerCheck }),
      ...(selectedLanguage.grammatical_check_language && { grammatical_check_language: selectedLanguage.grammatical_check_language, }),
      ...(typeof startConfig === "boolean" && { is_starting_config: startConfig }),
      auto_open: autoOpen,
      ...(typeof endConfig === "boolean" && { is_end_config: endConfig }),
      // Add custom storage fields at root level for upload config
      ...(chipLabel === 'upload' && customStorageFields),
      // Add advanced AI validation config for upload config
      ...(chipLabel === 'upload' && {
        ai_validation: advancedAiConfig ? buildAiValidationPayload(advancedAiConfig) : null,
      }),
      // ...(typeof enableUrl === "boolean" && { is_url_enabled: enableUrl }),
      ...(selectedStage && {
        stage_id: typeof selectedStage === "string" && !isNaN(Number(selectedStage))
          ? Number(selectedStage)
          : selectedStage
      }),
      ...(typeof configSticky === "boolean" && { is_sticky: configSticky }),
      ...(configType && (interactiveConfig === false || chipLabel === 'edit_content' || chipLabel === 'edit_code' || chipLabel === 'edit_latex')) && { default_value_type: configType },
      ...(defaultValueType && (interactiveConfig === false || chipLabel === 'edit_content' || chipLabel === 'edit_code' || chipLabel === 'edit_latex')) && { default_value: defaultValueType },
      enable_scoring: enableScoring,
      ...(enableScoring && { scoring_mode: scoringMode }),
      scoring_title: (enableScoring && scoringMode === 'MANUAL' && scoringTitle.trim()) ? scoringTitle.trim() : null,


    };

    // const newUpdateValues = Object.keys(updateValues).reduce((acc: any, key: any) => {
    //   if (updateValues[key] !== initialValues[key]) {
    //     acc[key] = updateValues[key];
    //   }
    //   return acc;
    // }, {});

    const newUpdateValues = Object.keys(initialValues).reduce((acc, key) => {
      const fetchedValue = configValue[key];
      const newValue = initialValues[key];

      if (key === "value") {
        const oldValue = fetchedValue || {};
        const newVal = newValue || {};

        const oldScore = JSON.stringify(oldValue?.score_values || {});
        const newScore = JSON.stringify(newVal?.score_values || {});
        const { score_values: _, ...oldRest } = oldValue;
        const { score_values: __, ...newRest } = newVal;
        const restChanged = JSON.stringify(oldRest) !== JSON.stringify(newRest);
        const scoreChanged = oldScore !== newScore;

        if (restChanged || scoreChanged) {
          acc[key] = newValue;
        }

        return acc;
      }

      // Always include custom storage fields and upload_storage for upload configs to ensure they're properly updated
      if (key === "upload_storage" ||
        key === "custom_upload_storage_provider_access_key_id" ||
        key === "custom_upload_storage_provider_secret_access_key" ||
        key === "custom_upload_storage_provider_region" ||
        key === "custom_upload_storage_provider_bucket_name" ||
        key === "custom_upload_storage_provider_endpoint_url" ||
        key === "custom_file_name" ||
        key === "keep_file_name_intact") {
        acc[key] = newValue;
        return acc;
      }

      if (
        key in configValue &&
        newValue !== fetchedValue &&
        fetchedValue !== ""
      ) {
        acc[key] = newValue;
      }

      return acc;
    }, {} as Record<string, string | boolean | string[] | undefined>);

    if (config_id) {
      // dispatch(updateConfig(updateValues));
      dispatch(updateConfig({ id: config_id, ...newUpdateValues }));
    } else {
      dispatch(createConfig(initialValues));
    }
  };


  useEffect(() => {
    const handleCreateStatus = () => {
      if (createStatus === STATUS.SUCESS) {
        // handleSnackbar('Config Added Successfully', 'success');
        handleSnackbar(createConfigMessage, "success");
        setTimeout(() => {
          navigate(`/project/${id}`);
        }, 1000);
      }
      if (createStatus === STATUS.FAILED) {
        // handleSnackbar('error occured please try again', 'warning');
        handleSnackbar(createConfigMessage, "warning");
      }
    };
    handleCreateStatus();
    return () => {
      if (createStatus === STATUS.SUCESS || createStatus === STATUS.FAILED) {
        dispatch(setCreateConfigStatus(STATUS.NOT_STARTED));
      }
    };
  }, [createStatus, navigate, dispatch]);

  useEffect(() => {
    const handleUpdateStatus = () => {
      if (updateStatus === STATUS.SUCESS) {
        // handleSnackbar('Config Updated Successfully', 'success');
        handleSnackbar(updateConfigMessage, "success");
        setTimeout(() => {
          navigate(`/project/${id}`);
        }, 1000);
      }

      if (updateStatus === STATUS.FAILED) {
        // handleSnackbar('Error Occurred, Please try again!', 'error');
        handleSnackbar(updateConfigMessage, "error");
      }
    };

    handleUpdateStatus();
    return () => {
      if (updateStatus == STATUS.SUCESS || updateStatus == STATUS.FAILED) {
        dispatch(setUpdateConfigStatus(STATUS.NOT_STARTED));
      }
    };
  }, [updateStatus, navigate, dispatch]);

  if (!keys) {
    return null;
  }

  const handleChangeLanguage = (
    event: SelectChangeEvent<string>,
    fieldName: string
  ) => {
    const value = event.target.value;
    setSelectedLanguage((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleFieldChange = (key: string, value: string) => {
    setFieldValues((prevValues) => ({
      ...prevValues,
      [key]: value,
    }));
    handleInputChange(key, value);
  };

  const handleMetaDataFieldChange = (
    metaKey: string,
    value: string[]
  ) => {
    setMetaDataList((prev) => ({
      ...prev,
      [metaKey]: value,
    }));

  };


  const handleStickyCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setConfigSticky(checked);
  };

  const handleDropDownChange = (index: number, value: string) => {
    // handleTagChange(index, value);
    setNextConfigId(configIds.find((item) => item.identifier === value));
  };

  //const languages: Language[] = ["Python", "PHP", "Java", "JavaScript"];
  const languages = [
    { label: "Java", value: "java", subValue: "java" },
    { label: "JavaScript", value: "javascript", subValue: "javascript" },
    { label: "Python & Django", value: "python", subValue: "pythondjango" },
    { label: "C++", value: "cpp", subValue: "cpp" },
    { label: "C", value: "c", subValue: "c" },
    { label: "C#", value: "csharp", subValue: "csharp" },
    { label: "TypeScript", value: "typescript", subValue: "typescript" },
    { label: "Go", value: "go", subValue: "go" },
    { label: "Rust", value: "rust", subValue: "rust" },
    { label: "PHP", value: "php", subValue: "php" },
    { label: "Shell", value: "shell", subValue: "shell" },
    { label: "Kotlin", value: "kotlin", subValue: "kotlin" },
    { label: "ObjC", value: "objective-c", subValue: "objc" },
    { label: "HTML/CSS", value: "html", subValue: "htmlcss" },
    { label: "Dart", value: "dart", subValue: "dart" },
    { label: "Ruby", value: "ruby", subValue: "ruby" },
    // { label: "Swift", value: "swift", subValue: "swift" },
    { label: "Tex", value: "latex", subValue: "tex" },
    { label: "Lua", value: "lua", subValue: "lua" },
    { label: "Swift (for iOS)", value: "swift", subValue: "swift" },
    { label: "Kotlin (for Android)", value: "kotlin", subValue: "kotlin" },
    { label: "Flutter", value: "dart", subValue: "flutter" },
    { label: "React Native", value: "javascript", subValue: "reactnative" },
    { label: "Vue.js", value: "javascript", subValue: "vuejs" },
    { label: "Angular", value: "typescript", subValue: "angular" },
    { label: "Next.js", value: "javascript", subValue: "nextjs" },
    { label: "Node.js", value: "javascript", subValue: "nodejs" },
    { label: "Flask", value: "python", subValue: "flask" },
    { label: "FastAPI", value: "python", subValue: "fastapi" },
    { label: "Blazor", value: "csharp", subValue: "blazor" },
    { label: "Ruby on Rails", value: "ruby", subValue: "rubyonrails" },
    { label: "Spring Boot", value: "java", subValue: "spring_boot" }
  ];

  const languagesWithCompiler: Record<string, boolean> = {
    javascript: true,
    python: true,
    pythondjango: true,
    java: true,
    cpp: true,
    c: true,
    ruby: true,
  };

  const hasCompiler = (languageValue: string): boolean => {
    return languagesWithCompiler[languageValue] || false;
  };

  const handleLongDesc = (content: { json: string; html: string }) => {
    setFormValues((prevState: any) => ({
      ...prevState,
      long_description: content.html,
    }));
  };

  const handleConfigTypeChange = (html: string) => {
    setDefaultValueType(html);
  };

  const handleLlmChange = (index: number, field: string, value: any) => {
    setValidLlmValues((prev: any[]) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  const handleLlmIntegrationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setLlmIntegration(checked);
  };

  const handleEndpointUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    setEndpointUrlInput(value);
    setEndpointCursorPosition(cursorPos);

    // Check if @ was just typed
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Show dropdown if @ is at the end or followed by text without space or /
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('/')) {
        setShowEndpointDropdown(true);
      } else {
        setShowEndpointDropdown(false);
      }
    } else {
      setShowEndpointDropdown(false);
    }

    handleFieldChange('custom_upload_storage_provider_endpoint_url', value);
  };

  const handleEndpointOptionSelect = (selectedValue: string, configId?: string) => {
    const textBeforeCursor = endpointUrlInput.substring(0, endpointCursorPosition);
    const textAfterCursor = endpointUrlInput.substring(endpointCursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const beforeAt = endpointUrlInput.substring(0, lastAtIndex);
      const newValue = beforeAt + selectedValue + '/' + textAfterCursor;

      // If configId is provided (for identifiers), store the mapping
      if (configId) {
        const identifier = selectedValue.replace(/{|}/g, ''); // Extract identifier without braces
        setIdentifierToIdMap(prev => ({
          ...prev,
          [identifier]: configId
        }));
      }

      setEndpointUrlInput(newValue);
      handleFieldChange('custom_upload_storage_provider_endpoint_url', newValue);
      setShowEndpointDropdown(false);

      // Set cursor position after the inserted value and /
      setTimeout(() => {
        if (endpointInputRef.current) {
          const newCursorPos = beforeAt.length + selectedValue.length + 1;
          endpointInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
          endpointInputRef.current.focus();
        }
      }, 0);
    }
  };

  const getEndpointUrlOptions = () => {
    // Default Values section - 9 fields
    const defaultValues = [
      { label: 'sub_project_id', value: '{sub_project_id}', section: 'Default Values' },
      { label: 'expert_id', value: '{expert_id}', section: 'Default Values' },
      { label: 'today_date', value: '{today_date}', section: 'Default Values' },
      { label: 'gigsource_id', value: '{gigsource_id}', section: 'Default Values' },
      { label: 'tag_1', value: '{tag_1}', section: 'Default Values' },
      { label: 'tag_2', value: '{tag_2}', section: 'Default Values' },
      { label: 'tag_3', value: '{tag_3}', section: 'Default Values' },
      { label: 'tag_4', value: '{tag_4}', section: 'Default Values' },
      { label: 'tag_5', value: '{tag_5}', section: 'Default Values' },
    ];

    // Task Data section
    const taskData = options.map((opt: any) => {
      // Handle different object structures
      let labelValue = '';
      if (typeof opt === 'string') {
        labelValue = opt;
      } else if (opt.identifier) {
        labelValue = opt.identifier;
      } else if (opt.key) {
        labelValue = opt.key;
      } else if (opt.name) {
        labelValue = opt.name;
      } else if (opt.value) {
        labelValue = typeof opt.value === 'string' ? opt.value : '';
      }

      return {
        label: labelValue,
        value: `{${labelValue}}`,
        section: 'Task Data'
      };
    }).filter(opt => opt.label !== ''); // Filter out empty labels

    // Identifiers section - only show single_select and multi_level_dropdown configs
    const identifiers = configIds
      .filter((config: any) =>
        config.identifier &&
        config.config_details &&
        (config.config_details.value === 'single_select' || config.config_details.value === 'multi_level_dropdown')
      )
      .map((config: any) => {
        const identifierWithUnderscore = config.identifier.replace(/\s+/g, '_');
        return {
          label: config.identifier,
          value: `{${identifierWithUnderscore}}`,
          section: 'Identifiers',
          configId: config.id // Include config ID for payload transformation
        };
      });

    return {
      defaultValues,
      taskData,
      identifiers
    };
  };





  return (
    <>

      <div
        style={{
          display: "flex",
          marginBottom: "20px",
          alignItems: "flex-start",
        }}
      >
        <div style={{ width: "400px" }}>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "24px",
              color: "#10383D",
              marginTop: "5px",
            }}
          >
            Enter Unique Identifier:
          </Typography>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "24px",
              color: "#10383D",
            }}
          >
            This Name will identify unique configs
          </Typography>
        </div>

        <div style={{ flex: 1 }}>
          <TextField
            placeholder="Enter Identifier"
            name="formValues"
            value={formValues.identifier}
            onChange={(e) => {
              const value = e.target.value;
              const sanitizedValue = value.replace(/[^a-zA-Z0-9_]/g, "");
              setFormValues((prev: any) => ({
                ...prev,
                identifier: sanitizedValue,
              }));
            }}
            // long_description: formValues.long_description,
            sx={{ marginTop: "10px" }}
            size="small"
          />
        </div>
      </div>


      <div
        style={{
          display: "flex",
          marginBottom: "20px",
          alignItems: "flex-start",
        }}
      >
        <div style={{ width: "400px" }}>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "24px",
              letterSpacing: "0px",
              textAlign: "left",
              color: "#10383D",
              marginTop: "5px",
            }}
          >
            Enter Value
          </Typography>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "24px",
              letterSpacing: "0px",
              textAlign: "left",
              color: "#10383D",
            }}
          >
            {" "}
            This heading will be visible on expert side{" "}
          </Typography>
        </div>

        <div
          style={{
            flex: 1,
            display: "flex",
            flexWrap: "wrap",
            marginTop: "10px",
          }}
        >
          {(chipLabel === 'video' && !keys.includes('format') ? [...keys, 'format'] : keys).map((key, index) => {
            const formattedLabel = key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (char) => char.toUpperCase());
            return (
              <div style={{ marginRight: "20px" }} key={index}>
                {(key === "max_file_size_in_kb" && chipLabel === 'upload') ? null
                : key === "selected_file_types" ? (
                  chipLabel === 'upload' ? null : (
                    <FormControl
                      fullWidth
                      size="small"
                      variant="outlined"
                      style={{ marginBottom: "10px" }}
                    >
                      <InputLabel>File Type</InputLabel>
                      <Select
                        multiple
                        label="File Type"
                        value={fieldValues[key] || ["pdf"]}
                        onChange={(e) =>
                          handleFieldChange(key, e.target.value as any)
                        }
                        style={{ width: "200px" }}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {(selected as string[]).map((val) => (
                              <Chip key={val} label={val.toUpperCase()} />
                            ))}
                          </Box>
                        )}
                      >
                        {Object.values(FileType).map((type) => (
                          <MenuItem key={type} value={type}>
                            {type.toUpperCase()}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ))
                : key === "language" ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" style={{ minWidth: "170px" }}>
                      <InputLabel id="language-select-label">
                        Choose Language
                      </InputLabel>
                      <Select
                        size="small"
                        labelId="language-select-label"
                        id="language-select"
                        value={fieldValues[key] || []}
                        label="Choose Language"
                        onChange={(e) => {
                          const selectedValue = e.target.value as string;
                          handleFieldChange(key, e.target.value as any)
                          setShowCompilerChip(hasCompiler(selectedValue));
                        }
                        }
                        renderValue={(selected) => {
                          const selectedLang = languages.find(lang => lang.subValue === selected);
                          return selectedLang ? selectedLang.label : '';
                        }}
                      >
                        {languages.map((language) => (
                          <MenuItem key={language.label} value={language.subValue}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                              <Typography>{language.label}</Typography>
                              {hasCompiler(language.subValue) && (
                                <Chip
                                  label="Compiler Included"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  sx={{
                                    fontFamily: "Montserrat",
                                    fontSize: "10px",
                                    height: "20px",
                                    fontWeight: 500,
                                  }}
                                />
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {showCompilerChip && (
                      <Chip
                        label="Compiler Included"
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{
                          fontFamily: "Montserrat",
                          fontSize: "12px",
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </Box>
                ) : key === "is_trim" ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={fieldValues[key] === "true"}
                        onChange={(e) =>
                          handleFieldChange(key, e?.target?.checked?.toString())
                        }
                      />
                    }
                    label="Show Loop Controls"
                  />
                ) : key === "format" ? (
                  <FormControl size="small" style={{ minWidth: "170px" }}>
                    <InputLabel id="format-select-label">
                      Time Format
                    </InputLabel>
                    <Select
                      size="small"
                      labelId="Time-Format"
                      id="format-select"
                      value={fieldValues[key] || ""}
                      label="Time Format"
                      onChange={(e) =>
                        handleFieldChange(key, e.target.value as string)
                      }
                    >
                      {formatOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    size="small"
                    label={formattedLabel}
                    variant="outlined"
                    value={fieldValues[key] || ""}
                    onChange={(e) => handleFieldChange(key, e.target.value)}
                    style={{ marginBottom: "10px" }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {chipLabel === "llm_response" && (
        <Box display="flex" flexDirection="row" alignItems="flex-start" gap="143px" mb={2}>
          <Box sx={{ minWidth: 200 }}>
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: "14px",
                fontWeight: 600,
                color: "#10383D",
                mb: 0.5,
              }}
            >
              Enter LLM Response Instructions:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: "12px",
                fontWeight: 400,
                color: "#10383D",
              }}
            >
              This would be visible on Expert Side.
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" flex={1}>
            {validLlmValues && validLlmValues?.map((llm: any, index: any) => (
              <div
                key={index}
                style={{
                  marginBottom: "10px",
                  padding: "10px",
                }}
              >
                <Box display="flex" flexWrap="wrap" alignItems="center" gap={2}>
                  <TextField
                    label="Select LLM Platform"
                    select
                    size="small"
                    value={llm.llmPlatform || ""}
                    onChange={(e) => handleLlmChange(index, 'llmPlatform', e.target.value)}
                    sx={{ minWidth: 200 }}
                  >
                    {["ChatGPT 5", "Claude 3.7", "Claude 4.2", "Nova Pro", "Nova Premier", "Gemini 2.5", "Claude 4.0"].map(
                      (option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      )
                    )}
                  </TextField>

                  <TextField
                    placeholder="Enter title for LLM response"
                    size="small"
                    value={llm.llmTitle || ""}
                    onChange={(e) => handleLlmChange(index, 'llmTitle', e.target.value)}
                    sx={{ minWidth: 250 }}
                  />
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography
                      sx={{
                        fontFamily: "Montserrat",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#10383D",
                      }}
                    >
                      Allow Edit:
                    </Typography>
                    <Checkbox
                      checked={llm.allowEdit || false}
                      onChange={(e) => handleLlmChange(index, 'allowEdit', e.target.checked)}
                    />
                  </Box>

                  <Box display="flex" alignItems="center">
                    {index === 0 ? (
                      <IconButton
                        style={{
                          backgroundColor: "#3D85C6",
                          color: "#fff",
                          padding: "7px",
                          borderRadius: "50%",
                          marginLeft: "5px",
                          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                          transition: "transform 0.3s ease",
                        }}
                        onClick={() =>
                          setValidLlmValues([
                            ...validLlmValues,
                            { llmPlatform: "", llmTitle: "", allowEdit: false },
                          ])
                        }
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.transform = "scale(1.1)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.transform = "scale(1)")
                        }
                      >
                        <AddIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        onClick={() =>
                          setValidLlmValues((prev: any[]) => prev.filter((_, i) => i !== index))
                        }
                        aria-label="delete row"
                        sx={{ marginLeft: 1, color: "#ec480f" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </div>
            ))}
          </Box>
        </Box>
      )}

      <div style={{ display: "flex", marginBottom: "20px", gap: 177 }}>
        <div>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "24px",
              letterSpacing: "0px",
              textAlign: "left",
              color: "#10383D",
              marginTop: "5px",
            }}
          >
            Enter Long Description:
            <br />
          </Typography>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "24px",
              letterSpacing: "0px",
              textAlign: "left",
              color: "#10383D",
            }}
          >
            {" "}
            This would be visible on Expert Side.{" "}
          </Typography>
        </div>

        <div>
          <RichTextEditor
            key={getConfigFromReducer?.id || "editor_placeholder_key"}
            onChange={handleLongDesc}
            initialContent={getConfigFromReducer?.long_description || ""}
          />
        </div>
      </div>

      {chipLabel === "score" && <div style={{
        display: "flex",
        gap: 177,
        marginBottom: "20px",
      }}>
        <div >
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "24px",
              letterSpacing: "0px",
              textAlign: "left",
              color: "#10383D",
              marginTop: "5px",
            }}
          >
            Select Score Value:
          </Typography>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "24px",
              letterSpacing: "0px",
              textAlign: "left",
              color: "#10383D",
            }}
          >
            {" "}
            This would be visible on Expert Side.{" "}
          </Typography>
        </div>
        <div>
          <ScoringSelector selectedFormat={selectedFormat} descriptions={descriptions}
            setSelectedFormat={setSelectedFormat} setDescriptions={setDescriptions}
            scoringFormats={scoringFormats} selectedEmojis={selectedEmojis} setSelectedEmojis={setSelectedEmojis}
            setPickerIndex={setPickerIndex} setPickerVisible={setPickerVisible} updatePickerPosition={updatePickerPosition}
            inputRefs={inputRefs} pickerVisible={pickerVisible} pickerPosition={pickerPosition} handleEmojiSelect={handleEmojiSelect}
            handleDescriptionChange={handleDescriptionChange}
          />
        </div>
      </div>}

      {(!interactiveConfig || chipLabel === "edit_content" || chipLabel === "edit_code" || chipLabel === "edit_latex") &&
        (<>

          <FormControl size="small" >
            <div style={{ display: 'flex', gap: 270 }}>
              <div>
                <Typography
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: "24px",
                    letterSpacing: "0px",
                    textAlign: "left",
                    color: "#10383D",
                    marginTop: "5px",
                  }}
                >
                  Config Data Type:
                </Typography>
              </div>
              <div style={{ display: "absolute", justifyContent: "space-around", alignItems: "center", gap: 10 }}>
                <div>
                  <Select
                    labelId="config-type-label"
                    id="config-type"
                    // label="Config Type"
                    value={configType}
                    onChange={(e) => setConfigType(e.target.value)}
                    sx={{ minWidth: 260, maxWidth: 350, alignItems: "flex-start", whiteSpace: "normal" }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          width: 320,
                          maxWidth: 320,
                          '& .MuiMenuItem-root': {
                            alignItems: 'flex-start',
                            whiteSpace: 'normal',
                          },
                        },
                      },
                    }}

                  >
                    {CONFIG_TYPES.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <ListItemText
                          primary={opt.label}
                          secondary={DESCRIPTIONS[opt.value]}
                          primaryTypographyProps={{ fontSize: 17, fontWeight: 400, lineHeight: 1.2, fontFamily: "montserrat" }}
                          secondaryTypographyProps={{
                            sx: {
                              mt: 0.5,
                              fontSize: 12,
                              color: 'text.secondary',
                              whiteSpace: 'normal',
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                              hyphens: 'auto',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            },
                          }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                {/* <div>
                  {configType &&
                    <RichTextEditor
                      key={configType}
                      // key={getConfigFromReducer?.id || "editor_placeholder_key"}
                      onChange={handleConfigTypeChange}
                      initialContent={getConfigFromReducer?.default_value || ""}
                      configType={configType}
                      mentionItems={options}
                      getStageWiseConfigData={getStageWiseConfigData}
                      stage_id={selectedCategory}
                    />
                  }
                </div> */}
                {configType && <CustomRichTextEditor
                  type={configType}
                  column_names={options}
                  previous_stages={getStageWiseConfigData.data?.results}
                  initialContent={getConfigFromReducer?.default_value || ""}
                  onContentChange={handleConfigTypeChange} />
                }
              </div>
            </div>
          </FormControl>
        </>
        )}

      <div style={{ display: "flex", gap: "155px" }}>
        <div>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "24px",
              letterSpacing: "0px",
              textAlign: "left",
              color: "#10383D",
              marginTop: "15px",
            }}
          >
            Do you want to stick this config?
          </Typography>
        </div>
        <div>
          <Checkbox
            checked={configSticky}
            onChange={handleStickyCheckboxChange}
            color="primary"
            sx={{ marginTop: "5px" }}
          />
        </div>
      </div>
      {chipLabel === "content" && (
        <div style={{ display: "flex", gap: 60 }}>
          <div>
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "24px",
                letterSpacing: "0px",
                textAlign: "left",
                color: "#10383D",
                marginTop: "15px",
              }}
            >
              Do you want to check for grammatical errors?
            </Typography>
          </div>
          <div style={{ flex: 3 }}>
            <Checkbox
              checked={grammerCheck}
              onChange={handleGrammerCheckboxChange}
              color="primary"
              sx={{ marginTop: "5px" }}
            />
          </div>
        </div>
      )}

      {chipLabel === "content" && (
        <div style={{ display: "flex", gap: "230px" }}>
          <div>
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "24px",
                letterSpacing: "0px",
                textAlign: "left",
                color: "#10383D",
                marginTop: "15px",
              }}
            >
              Enable URL Validation
            </Typography>
          </div>
          <div style={{ flex: 3 }}>
            <Checkbox
              checked={enableUrl}
              onChange={handleEnableUrlChange}
              color="primary"
              sx={{ marginTop: "5px" }}
            />
          </div>
        </div>
      )}

      {grammerCheck && (
        <div style={{ display: "flex", gap: 130 }}>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "24px",
              letterSpacing: "0px",
              textAlign: "left",
              color: "#10383D",
              marginTop: "15px",
            }}
          >
            Select Language
          </Typography>
          <FormControl sx={{ m: 1, minWidth: 200, marginBottom: "15px" }}>
            <Select
              label="Language"
              value={selectedLanguage?.grammatical_check_language}
              size="small"
              onChange={(e: any) =>
                handleChangeLanguage(e, "grammatical_check_language")
              }
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Language
              </MenuItem>
              <MenuItem value="de">German</MenuItem>
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="es">Spanish</MenuItem>
              <MenuItem value="fr">French</MenuItem>
              <MenuItem value="it">Italian</MenuItem>
            </Select>
          </FormControl>
        </div>
      )}

      <div style={{ display: "flex", gap: "260px" }}>
        <Typography
          sx={{
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "24px",
            letterSpacing: "0px",
            textAlign: "left",
            color: "#10383D",
            marginTop: "15px",
          }}
        >
          Select Next Config
        </Typography>
        {configStatus === "loading" && <Typography>Loading...</Typography>}

        {configStatus === "success" && (
          <FormControl
            sx={{
              m: 1,
              minWidth: 100,
              marginBottom: "15px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Select
              variant="standard"
              labelId={`demo-simple-select-label`}
              id={`demo-simple-select}`}
              label="Tag"
              value={nextConfigId !== undefined ? nextConfigId.identifier : ""}
              onChange={(e) => handleDropDownChange(0, e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Next Config
              </MenuItem>
              {filteredConfigs?.map(d => (
                <MenuItem key={d?.id} value={d?.identifier}>
                  {d?.identifier}
                </MenuItem>
              ))}
            </Select>
            {nextConfigId && (
              <Button
                onClick={handleClearSelectionNextConfig}
                size="small"
                sx={{ minWidth: "auto" }}
              >
                Clear
              </Button>
            )}
          </FormControl>
        )}
        {configStatus === "error" && (
          <Typography sx={{ color: "red" }}>
            Error fetching configs. Please try again.
          </Typography>
        )}
      </div>
      <div style={{ display: "flex", gap: "254px" }}>
        <Typography
          sx={{
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "24px",
            letterSpacing: "0px",
            textAlign: "left",
            color: "#10383D",
            marginTop: "15px",
          }}
        >
          Is this start config?
        </Typography>
        <Checkbox
          checked={startConfig}
          onChange={handleStartConfigCheckboxChange}
          color="primary"
          sx={{ marginTop: "5px" }}
        />
      </div>
      <div style={{ display: "flex", gap: "305px" }}>
        <Typography
          sx={{
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontWeight: 600,
            lineHeight: "24px",
            letterSpacing: "0px",
            textAlign: "left",
            color: "#10383D",
            marginTop: "15px",
          }}
        >
          Auto Open?
        </Typography>
        <Checkbox
          checked={autoOpen}
          onChange={handleAutoOpenCheckboxChange}
          color="primary"
          sx={{ marginTop: "5px" }}
        />
      </div>
      {chipLabel === "url" && (
        <div style={{ display: "flex", gap: "273px" }}>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "24px",
              letterSpacing: "0px",
              textAlign: "left",
              color: "#10383D",
              marginTop: "15px",
            }}
          >
            LLM Integration:
          </Typography>
          <Checkbox
            checked={llmIntegration}
            onChange={handleLlmIntegrationChange}
            color="primary"
            sx={{ marginTop: "5px" }}
          />
        </div>
      )}
      {chipLabel === "url" && llmIntegration && (
        <div style={{ display: "flex", marginBottom: "20px", alignItems: "flex-start" }}>
          <div style={{ width: "400px" }}>
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "24px",
                letterSpacing: "0px",
                textAlign: "left",
                color: "#10383D",
                marginTop: "5px",
              }}
            >
              Enter Prompt:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "24px",
                letterSpacing: "0px",
                textAlign: "left",
                color: "#10383D",
              }}
            >
              This prompt will be used for LLM integration
            </Typography>
          </div>
          <div style={{ width: "50%", maxWidth: "600px" }}>
            <TextField
              placeholder="Enter your prompt here"
              multiline
              rows={4}
              fullWidth
              value={fieldValues.prompt || ""}
              onChange={(e) => handleFieldChange("prompt", e.target.value)}
              sx={{ marginTop: "10px" }}
              size="small"
            />
          </div>
        </div>
      )}
      {chipLabel === "upload" && (
        <div style={{ display: "flex", gap: "24px" }}>
          <div>
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "24px",
                letterSpacing: "0px",
                textAlign: "left",
                color: "#10383D",
                marginTop: "15px",
              }}
            >
              Keep file name intact
            </Typography>
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: "11px",
                fontWeight: 400,
                lineHeight: "16px",
                color: "#10383D",
                marginTop: "2px",
              }}
            >
              Enabling this will overwrite any existing file with the same name.
            </Typography>
          </div>
          <Checkbox
            checked={keepFileNameIntact}
            onChange={handleKeepFileNameIntactChange}
            color="primary"
            sx={{ marginTop: "5px" }}
          />
        </div>
      )}

      {chipLabel === "upload" && (
        <div style={{ display: "flex", marginBottom: "20px", alignItems: "flex-start" }}>
          <div style={{ width: "400px" }}>
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: "14px",
                fontWeight: 600,
                lineHeight: "24px",
                color: "#10383D",
                marginTop: "5px",
              }}
            >
              Custom File Name:
            </Typography>
            <Typography
              sx={{
                fontFamily: "Montserrat",
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: "24px",
                color: "#10383D",
              }}
            >
              Type @ to insert variables (e.g., folder_@taskData_)
            </Typography>
          </div>
          <div style={{ width: "50%", maxWidth: "600px", position: "relative" }}>
            <TextField
              inputRef={customFileNameInputRef}
              placeholder="Type @ to insert variables"
              fullWidth
              value={customFileNameInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                const cursorPos = e.target.selectionStart || 0;
                setCustomFileNameInput(value);
                setCustomFileNameCursorPosition(cursorPos);
                const textBeforeCursor = value.substring(0, cursorPos);
                const lastAtIndex = textBeforeCursor.lastIndexOf('@');
                if (lastAtIndex !== -1) {
                  const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
                  setShowCustomFileNameDropdown(!textAfterAt.includes(' ') && !textAfterAt.includes('/') && !textAfterAt.includes('_'));
                } else {
                  setShowCustomFileNameDropdown(false);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setShowCustomFileNameDropdown(false);
              }}
              sx={{ marginTop: "10px" }}
              size="small"
            />
            {showCustomFileNameDropdown && (
              <Box
                sx={{
                  position: "fixed",
                  maxWidth: "600px",
                  width: "100%",
                  maxHeight: "250px",
                  overflowY: "auto",
                  backgroundColor: "white",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  zIndex: 9999,
                }}
                style={{
                  left: customFileNameInputRef.current?.getBoundingClientRect().left,
                  bottom: customFileNameInputRef.current
                    ? window.innerHeight - customFileNameInputRef.current.getBoundingClientRect().top + 4
                    : undefined,
                }}
              >
                {(() => {
                  const opts = getEndpointUrlOptions();
                  const handleSelect = (selectedValue: string, configId?: string) => {
                    const result = insertCustomFileNameSelection(
                      customFileNameInput,
                      customFileNameCursorPosition,
                      selectedValue,
                    );

                    if (configId) {
                      const identifier = selectedValue.replace(/{|}/g, '');
                      setCustomFileNameIdentifierToIdMap((prev) => ({ ...prev, [identifier]: configId }));
                    }

                    setCustomFileNameInput(result.nextValue);
                    setShowCustomFileNameDropdown(false);
                    setTimeout(() => {
                      if (customFileNameInputRef.current) {
                        customFileNameInputRef.current.setSelectionRange(
                          result.nextCursorPosition,
                          result.nextCursorPosition,
                        );
                        customFileNameInputRef.current.focus();
                      }
                    }, 0);
                  };
                  return (
                    <>
                      {opts.defaultValues.length > 0 && (
                        <ListSubheader sx={{ fontFamily: "Montserrat", fontSize: "12px", fontWeight: 600, color: "#10383D", backgroundColor: "#f5f5f5", lineHeight: "32px" }}>
                          Default Values
                        </ListSubheader>
                      )}
                      {opts.defaultValues.map((option, index) => (
                        <MenuItem key={`cfn-dv-${index}`} onClick={() => handleSelect(option.value)} sx={{ fontFamily: "Montserrat", fontSize: "14px", pl: 3, '&:hover': { backgroundColor: "#f0f0f0" } }}>
                          {option.label}
                        </MenuItem>
                      ))}
                      {opts.taskData.length > 0 && (
                        <ListSubheader sx={{ fontFamily: "Montserrat", fontSize: "12px", fontWeight: 600, color: "#10383D", backgroundColor: "#f5f5f5", lineHeight: "32px" }}>
                          Task Data
                        </ListSubheader>
                      )}
                      {opts.taskData.map((option, index) => (
                        <MenuItem key={`cfn-td-${index}`} onClick={() => handleSelect(option.value)} sx={{ fontFamily: "Montserrat", fontSize: "14px", pl: 3, '&:hover': { backgroundColor: "#f0f0f0" } }}>
                          {option.label}
                        </MenuItem>
                      ))}
                      {opts.identifiers.length > 0 && (
                        <ListSubheader sx={{ fontFamily: "Montserrat", fontSize: "12px", fontWeight: 600, color: "#10383D", backgroundColor: "#f5f5f5", lineHeight: "32px" }}>
                          Identifiers
                        </ListSubheader>
                      )}
                      {opts.identifiers.map((option, index) => (
                        <MenuItem key={`cfn-id-${index}`} onClick={() => handleSelect(option.value, option.configId)} sx={{ fontFamily: "Montserrat", fontSize: "14px", pl: 3, '&:hover': { backgroundColor: "#f0f0f0" } }}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </>
                  );
                })()}
              </Box>
            )}
          </div>
        </div>
      )}

      {chipLabel === "upload" && (
        <div style={{ display: "flex", gap: "170px" }}>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "24px",
              letterSpacing: "0px",
              textAlign: "left",
              color: "#10383D",
              marginTop: "15px",
            }}
          >
            Use Custom Storage Provider?
          </Typography>
          <Checkbox
            checked={useCustomStorage}
            onChange={handleCustomStorageCheckboxChange}
            color="primary"
            sx={{ marginTop: "5px" }}
          />
        </div>
      )}

      {useCustomStorage && chipLabel === "upload" && (
        <>
          <div style={{ display: "flex", marginBottom: "20px", alignItems: "flex-start" }}>
            <div style={{ width: "400px" }}>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "14px",
                  fontWeight: 600,
                  lineHeight: "24px",
                  color: "#10383D",
                  marginTop: "5px",
                }}
              >
                Access Key ID:
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "24px",
                  color: "#10383D",
                }}
              >
                Enter your storage provider access key ID
              </Typography>
            </div>
            <div style={{ width: "50%", maxWidth: "600px" }}>
              <TextField
                placeholder="Access Key ID"
                fullWidth
                value={fieldValues.custom_upload_storage_provider_access_key_id || ""}
                onChange={(e) => handleFieldChange("custom_upload_storage_provider_access_key_id", e.target.value)}
                sx={{ marginTop: "10px" }}
                size="small"
              />
            </div>
          </div>

          <div style={{ display: "flex", marginBottom: "20px", alignItems: "flex-start" }}>
            <div style={{ width: "400px" }}>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "14px",
                  fontWeight: 600,
                  lineHeight: "24px",
                  color: "#10383D",
                  marginTop: "5px",
                }}
              >
                Secret Access Key:
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "24px",
                  color: "#10383D",
                }}
              >
                Enter your storage provider secret access key
              </Typography>
            </div>
            <div style={{ width: "50%", maxWidth: "600px" }}>
              <TextField
                placeholder="Secret Access Key"
                type="password"
                fullWidth
                value={fieldValues.custom_upload_storage_provider_secret_access_key || ""}
                onChange={(e) => handleFieldChange("custom_upload_storage_provider_secret_access_key", e.target.value)}
                sx={{ marginTop: "10px" }}
                size="small"
              />
            </div>
          </div>

          <div style={{ display: "flex", marginBottom: "20px", alignItems: "flex-start" }}>
            <div style={{ width: "400px" }}>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "14px",
                  fontWeight: 600,
                  lineHeight: "24px",
                  color: "#10383D",
                  marginTop: "5px",
                }}
              >
                Region:
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "24px",
                  color: "#10383D",
                }}
              >
                Enter your storage provider region (e.g., us-east-1)
              </Typography>
            </div>
            <div style={{ width: "50%", maxWidth: "600px" }}>
              <TextField
                placeholder="us-east-1"
                fullWidth
                value={fieldValues.custom_upload_storage_provider_region || ""}
                onChange={(e) => handleFieldChange("custom_upload_storage_provider_region", e.target.value)}
                sx={{ marginTop: "10px" }}
                size="small"
              />
            </div>
          </div>

          <div style={{ display: "flex", marginBottom: "20px", alignItems: "flex-start" }}>
            <div style={{ width: "400px" }}>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "14px",
                  fontWeight: 600,
                  lineHeight: "24px",
                  color: "#10383D",
                  marginTop: "5px",
                }}
              >
                Bucket Name:
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "24px",
                  color: "#10383D",
                }}
              >
                Enter your storage bucket name
              </Typography>
            </div>
            <div style={{ width: "50%", maxWidth: "600px" }}>
              <TextField
                placeholder="Bucket Name"
                fullWidth
                value={fieldValues.custom_upload_storage_provider_bucket_name || ""}
                onChange={(e) => handleFieldChange("custom_upload_storage_provider_bucket_name", e.target.value)}
                sx={{ marginTop: "10px" }}
                size="small"
              />
            </div>
          </div>

          <div style={{ display: "flex", marginBottom: "20px", alignItems: "flex-start" }}>
            <div style={{ width: "400px" }}>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "14px",
                  fontWeight: 600,
                  lineHeight: "24px",
                  color: "#10383D",
                  marginTop: "5px",
                }}
              >
                Endpoint URL:
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "24px",
                  color: "#10383D",
                }}
              >
                Type @ to insert variables (e.g., p_@taskData/)
              </Typography>
            </div>
            <div style={{ width: "50%", maxWidth: "600px", position: "relative" }}>
              <TextField
                inputRef={endpointInputRef}
                placeholder="Type @ to insert variables"
                fullWidth
                value={endpointUrlInput}
                onChange={handleEndpointUrlChange}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setShowEndpointDropdown(false);
                  }
                }}
                sx={{ marginTop: "10px" }}
                size="small"
              />
              {showEndpointDropdown && (
                <Box
                  sx={{
                    position: "fixed",
                    top: "auto",
                    bottom: "auto",
                    maxWidth: "600px",
                    width: "100%",
                    maxHeight: "250px",
                    overflowY: "auto",
                    backgroundColor: "white",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    zIndex: 9999,
                  }}
                  style={{
                    left: endpointInputRef.current?.getBoundingClientRect().left,
                    bottom: endpointInputRef.current
                      ? window.innerHeight - endpointInputRef.current.getBoundingClientRect().top + 4
                      : undefined,
                  }}
                >
                  {(() => {
                    const endpointOptions = getEndpointUrlOptions();
                    return (
                      <>
                        {/* Default Values Section */}
                        {endpointOptions.defaultValues.length > 0 && (
                          <>
                            <ListSubheader
                              sx={{
                                fontFamily: "Montserrat",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "#10383D",
                                backgroundColor: "#f5f5f5",
                                lineHeight: "32px",
                              }}
                            >
                              Default Values
                            </ListSubheader>
                            {endpointOptions.defaultValues.map((option, index) => (
                              <MenuItem
                                key={`default-${index}`}
                                onClick={() => handleEndpointOptionSelect(option.value)}
                                sx={{
                                  fontFamily: "Montserrat",
                                  fontSize: "14px",
                                  pl: 3,
                                  '&:hover': {
                                    backgroundColor: "#f0f0f0",
                                  },
                                }}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </>
                        )}

                        {/* Task Data Section */}
                        {endpointOptions.taskData.length > 0 && (
                          <>
                            <ListSubheader
                              sx={{
                                fontFamily: "Montserrat",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "#10383D",
                                backgroundColor: "#f5f5f5",
                                lineHeight: "32px",
                              }}
                            >
                              Task Data
                            </ListSubheader>
                            {endpointOptions.taskData.map((option, index) => (
                              <MenuItem
                                key={`task-${index}`}
                                onClick={() => handleEndpointOptionSelect(option.value)}
                                sx={{
                                  fontFamily: "Montserrat",
                                  fontSize: "14px",
                                  pl: 3,
                                  '&:hover': {
                                    backgroundColor: "#f0f0f0",
                                  },
                                }}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </>
                        )}

                        {/* Identifiers Section */}
                        {endpointOptions.identifiers.length > 0 && (
                          <>
                            <ListSubheader
                              sx={{
                                fontFamily: "Montserrat",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "#10383D",
                                backgroundColor: "#f5f5f5",
                                lineHeight: "32px",
                              }}
                            >
                              Identifiers
                            </ListSubheader>
                            {endpointOptions.identifiers.map((option, index) => (
                              <MenuItem
                                key={`identifier-${index}`}
                                onClick={() => handleEndpointOptionSelect(option.value, option.configId)}
                                sx={{
                                  fontFamily: "Montserrat",
                                  fontSize: "14px",
                                  pl: 3,
                                  '&:hover': {
                                    backgroundColor: "#f0f0f0",
                                  },
                                }}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                          </>
                        )}
                      </>
                    );
                  })()}
                </Box>
              )}
            </div>
          </div>
        </>
      )}
      {/* <div style={{ display: 'flex' }}>
        <Typography sx={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: '24px', letterSpacing: '0px', textAlign: 'left', color: '#10383D', marginTop: '15px' }}>
          Is this last config?
        </Typography>
        <Checkbox
          checked={endConfig}
          onChange={handleEndConfigCheckboxChange}
          color="primary"
          sx={{ marginTop: '5px' }}
        />
      </div> */}
      <ScoringConfigSection
        enableScoring={enableScoring}
        scoringMode={scoringMode}
        scoringTitle={scoringTitle}
        onEnableScoringChange={setEnableScoring}
        onScoringModeChange={setScoringMode}
        onScoringTitleChange={setScoringTitle}
        gap={286}
      />
      <div style={{ display: "flex", justifyContent: "space-around", gap: "152px" }}>
        <div >
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "24px",
              letterSpacing: "0px",
              textAlign: "left",
              color: "#10383D",
              marginTop: "15px",
            }}
          >
            Do you want to mark it required?
          </Typography>
        </div>
        <div style={{ flex: 3 }}>
          <Checkbox
            checked={localIsChecked}
            onChange={handleCheckboxChange}
            color="primary"
            sx={{ marginTop: "5px" }}
          />
        </div>
      </div>
      {chipLabel === 'upload' && (
        <Box sx={{ my: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <div
              onClick={() => setShowAiPanel(prev => !prev)}
            >
             <Typography sx={{
              fontFamily: "Montserrat",
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: "24px",
              letterSpacing: "0px",
              textAlign: "left",
              color: "#10383D",
              cursor:'pointer'
            }}>
               Advanced Validation {showAiPanel ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
             </Typography>
            </div>
            <Box>
              {config_id && (
                <Button
                  variant="contained"
                  disableElevation
                  onClick={() => setShowTestAiModal(true)}
                  sx={{
                    fontFamily: 'Montserrat',
                    fontSize: '13px',
                    fontWeight: 600,
                    textTransform: 'none',
                    backgroundColor: '#3D85C6',
                    color: '#ffffff',
                    borderRadius: '8px',
                    px: 2,
                    py: 0.75,
                    '&:hover': { backgroundColor: '#2f6ba0' },
                  }}
                >
                  Test Advanced Validation
                </Button>
              )}
            </Box>
          </Box>
          <Collapse in={showAiPanel} unmountOnExit>
            <AdvancedAiSettings
              key={getConfigFromReducer?.updated_at}
              onSave={() => { handleSubmit(); setShowAiPanel(false); }}
              onCancel={() => setShowAiPanel(false)}
              onConfigChange={(config) => setAdvancedAiConfig(config)}
              initialConfig={aiValidationInitialConfig}
              configIdentifiers={configIds.map(c => ({ id: c.id, identifier: c.identifier, type: c.config_details.value, value: c.value }))}
              useCustomStorage={useCustomStorage}
              endpointUrlInput={endpointUrlInput}
            />
          </Collapse>
        </Box>
      )}
      {/* ── Audio Mode Settings (audio_recording only) ── */}
      {chipLabel === 'audio_recording' && (
        <Box sx={{ my: 2 }}>
          <Box
            onClick={() => setShowAudioModePanel((prev) => !prev)}
            sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
          >
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: '24px',
                color: '#10383D',
              }}
            >
              Audio Mode Settings {showAudioModePanel ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </Typography>
          </Box>
          <Collapse in={showAudioModePanel}>
            <Box sx={{ mt: 2, p: 2, border: '1px solid #e5e7eb', borderRadius: 2, backgroundColor: '#fafafa' }}>
              <AudioModeSettings
                key={audioModeInitialConfig ? 'hydrated' : 'empty'}
                initialConfig={audioModeInitialConfig}
                onConfigChange={(config) => { setAudioModeConfig(config); setShowAudioErrors(false); }}
                columnNames={options}
                previousStages={getStageWiseConfigData.data?.results}
                initialScriptContent={getConfigFromReducer?.default_value || ''}
                showErrors={showAudioErrors}
              />
            </Box>
          </Collapse>
        </Box>
      )}
        <AddButton
          disabled={
            config_id
              ? updateStatus == STATUS.PENDING
              : createStatus == STATUS.PENDING
          }
          startIcon={
            (config_id && updateStatus === STATUS.PENDING) ||
              createStatus === STATUS.PENDING ? (
              <CircularProgress size={20} />
            ) : null
          }
          onClick={handleSubmit}
        >
          {config_id ? "Update" : "Submit"}
        </AddButton>
      <SnackbarNotification
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
      <TestAiValidationModal
        open={showTestAiModal}
        onClose={() => setShowTestAiModal(false)}
        initialPrompt={advancedAiConfig?.customPrompt?.text ?? ''}
        isCustomPromptEnabled={advancedAiConfig?.customPrompt?.enabled ?? false}
        projectId={id}
        configId={config_id}
        allowedFormats={
          advancedAiConfig?.metadataFields
            ?.find(f => f.id === 'fileformat')
            ?.value
            ?.split(',')
            .map(v => v.trim())
            .filter(Boolean)
        }
      />
    </>
  );
};

export default DictTypeContent;
